/*!
 * scopeBarcode.js — view:barcode: a sound's spectrum as black and white bars
 * © 2026 Rob Canning — GPLv3
 *
 *     viz(s, view:barcode)             64 columns, low to high (log)
 *     viz(s, view:barcode, rows:128)   more of them, thinner
 *     viz(s, view:barcode, step:0.5)   a new barcode every half beat (scope.js), not every frame
 *     viz(s, ..., step:0.5, tween:1)   ... each change taking the whole step: a bar coming
 *                                      grows from its middle, one going shrinks away, eased
 *     viz(s, ..., scroll:right)        a conveyor: a bar in at the left edge every step: (a
 *                                      sixteenth by default), the strip moving steadily across
 *                                      and off the right -- left, and up / down (bars across) too;
 *                                      each new bar the next band of the spectrum, in turn
 *     viz(s, ..., scroll:right, step:0.25, tween:0.5)   a ticker: a bar a step, gliding over
 *                                      half of it (eased), then resting -- the bar coming in
 *                                      growing from its middle, the one going out shrinking away
 *
 * Each column a band of the spectrum; a column is a bar while its band is
 * louder than its own recent average (and above a floor): the bars switch
 * with the sound, neighbours running together into wider ones -- a barcode
 * that reads the music. Painted on the box's canvas each frame (scope.js),
 * the full height of the box, in its colour. Pure but for the canvas.
 */

import { levelAt } from "./scopeViews.js";

export const BARCODE_COLUMNS = 64;

/** Each column's level now, 0..1 (its band of the spectrum). */
export function barcodeLevels(freq, sampleRate, n) {
  return Float32Array.from({ length: n }, (_, j) => levelAt(freq, (j + 0.5) / n, sampleRate));
}

/** A first barcode from the levels alone: the bands above their middle (median) on -- about half of them. */
export function barcodeSeed(levels) {
  const mid = [...levels].sort((a, b) => a - b)[levels.length >> 1];
  return Uint8Array.from(levels, l => (l > mid ? 1 : 0));
}

/** One frame's bars: 1 or 0 per column. `st` keeps each column's recent average. */
export function barcodeBars(freq, sampleRate, n, st, { floor = 0.12, over = 1.05, keep = 0.92 } = {}) {
  // the averages start at the levels now: at 0 every band was above its own on the first frame -- all white
  if (!st.avg || st.avg.length !== n) st.avg = barcodeLevels(freq, sampleRate, n);
  const on = new Uint8Array(n);
  for (let j = 0; j < n; j++) {
    const l = levelAt(freq, (j + 0.5) / n, sampleRate);
    on[j] = l > floor && l > st.avg[j] * over ? 1 : 0;
    st.avg[j] = st.avg[j] * keep + l * (1 - keep);
  }
  return on;
}

/** The bars on `canvas` (showing `area` of the box), each run of them one rectangle across `draw`. */
export function paintBarcode(canvas, area, draw, on, color) {
  const g = canvas.getContext("2d");
  const sx = canvas.width / area.width, sy = canvas.height / area.height;
  g.clearRect(0, 0, canvas.width, canvas.height);
  g.fillStyle = color;
  const n = on.length, colW = draw.width / n;
  const y0 = (draw.y - area.y) * sy, h = draw.height * sy;
  for (let j = 0; j < n; j++) {
    if (!on[j]) continue;
    let k = j;
    while (k + 1 < n && on[k + 1]) k++;
    const x0 = (draw.x + j * colW - area.x) * sx, x1 = (draw.x + (k + 1) * colW - area.x) * sx;
    g.fillRect(x0, y0, Math.max(1, x1 - x0), h);
    j = k;
  }
}

/** Ease in and out (smoothstep), 0..1. */
export const easeStep = t => t * t * (3 - 2 * t);
const ease = easeStep;

/**
 * The columns `t` (0..1) of the way from `from` (each 0..1, where the bars
 * were) to `to` (the new bars, 1 or 0), eased.
 */
export function barcodeBetween(from, to, t) {
  const k = ease(Math.min(1, Math.max(0, t)));
  return Float32Array.from(to, (v, j) => (from?.[j] ?? 0) + (v - (from?.[j] ?? 0)) * k);
}

/**
 * Columns part way (each 0..1): a whole one a bar its column's width (runs of
 * them one rectangle), a part one a bar that much of it, about its middle.
 */
export function paintBarcodeLevels(canvas, area, draw, levels, color) {
  const g = canvas.getContext("2d");
  const sx = canvas.width / area.width, sy = canvas.height / area.height;
  g.clearRect(0, 0, canvas.width, canvas.height);
  g.fillStyle = color;
  const n = levels.length, colW = draw.width / n;
  const y0 = (draw.y - area.y) * sy, h = draw.height * sy;
  const X = x => (x - area.x) * sx;
  for (let j = 0; j < n; j++) {
    const v = levels[j];
    if (v <= 0.002) continue;
    if (v >= 0.998) {
      let k = j;
      while (k + 1 < n && levels[k + 1] >= 0.998) k++;
      const x0 = X(draw.x + j * colW), x1 = X(draw.x + (k + 1) * colW);
      g.fillRect(x0, y0, Math.max(1, x1 - x0), h);
      j = k;
      continue;
    }
    const mid = draw.x + (j + 0.5) * colW, w = v * colW;
    g.fillRect(X(mid - w / 2), y0, Math.max(0.5, w * sx), h);
  }
}

export const SCROLLS = new Set(["right", "left", "up", "down"]);

/**
 * The conveyor's strip moved on to step `idx`: a new bar in at its front
 * (strip[0]) for each step since the last, each the next band of `fresh` in
 * turn, the oldest dropped. `st` keeps the strip, the step it is at, the band.
 */
export function scrollOn(st, fresh, n, idx, seed = fresh) {
  if (!st.strip || st.strip.length !== n + 1 || st.idx == null || idx - st.idx > n + 1 || idx < st.idx) {
    // a new strip starts full -- the box filled at once (from `seed`: a first barcode), not empty, waiting to fill
    st.strip = Uint8Array.from({ length: n + 1 }, (_, i) => seed[i % seed.length]);
    st.idx = idx;
    st.k = n + 1;
  }
  while (st.idx < idx) {
    st.strip.copyWithin(1, 0, n);
    st.strip[0] = fresh[st.k % fresh.length];
    st.k++;
    st.idx++;
  }
  return st.strip;
}

/**
 * The strip painted, `frac` (0..1) of a bar's width on from the step it is
 * at: right (in at the left), left, down (in at the top), up. `grow` (0..1,
 * or null): the bar coming in that much of its width, the one going out the
 * rest -- each about its own middle (tween:).
 */
export function paintBarcodeScroll(canvas, area, draw, strip, frac, dir, color, grow = null) {
  const g = canvas.getContext("2d");
  const sx = canvas.width / area.width, sy = canvas.height / area.height;
  g.clearRect(0, 0, canvas.width, canvas.height);
  g.fillStyle = color;
  const n = strip.length - 1;
  const across = dir === "right" || dir === "left";
  const size = (across ? draw.width : draw.height) / n;
  const partial = i => grow != null && (i === 0 || i === n);
  for (let i = 0; i <= n; i++) {
    if (!strip[i]) continue;
    // a run of bars side by side one rectangle: drawn apart, their edges left a seam
    // (the one coming in and the one going out, part-width under tween:, alone)
    let k = i;
    if (!partial(i)) while (k + 1 <= n && strip[k + 1] && !partial(k + 1)) k++;
    // where bar i is along the way it moves: just outside the start edge when new (i 0, frac 0)
    let along = (i - 1 + frac) * size, len = (k - i + 1) * size;
    if (partial(i)) {
      const w = i === 0 ? grow : 1 - grow;
      along += (1 - w) * size / 2;
      len = w * size;
      if (len <= 0.01) { i = k; continue; }
    }
    if (across) {
      const x = dir === "right" ? draw.x + along : draw.x + draw.width - along - len;
      g.fillRect((x - area.x) * sx, (draw.y - area.y) * sy, len * sx, draw.height * sy);
    } else {
      const y = dir === "down" ? draw.y + along : draw.y + draw.height - along - len;
      g.fillRect((draw.x - area.x) * sx, (y - area.y) * sy, draw.width * sx, len * sy);
    }
    i = k;
  }
}
